import React, {useContext, useEffect, useState} from 'react';
import type {ReactNode} from 'react';
import {getUser} from "../services/UserService";
import {impersonateUser as impersonateUserApi, stopImpersonation as stopImpersonationApi} from "../services/system/ImpersonationService";
import type {User} from "../api/generated";
import {ROLES, ADMIN_APP_ROLES} from "../enums/Roles";
import type {Capability} from "../enums/Capabilities";
// @ts-ignore — untyped JS module (TODO(ts-migration): convert sentry.js)
import {setSentryUser} from "../sentry";

// The identity returned by /user/me. The generated `User` DTO marks every field optional;
// `authorities` is the one the role predicates read.
// The identity returned by /user/me. `authorities` drives the role predicates; `capabilities` carries
// the delegated MANAGER grants (an ADMIN reports all of them).
export type AuthUser = User;

export interface AuthContextValue {
  currentUser: AuthUser | undefined;
  refetchUser: () => void;
  login: (user: AuthUser | undefined, token: string) => void;
  logout: () => void;
  isSuperAdmin: () => boolean;
  isCompanyAdmin: () => boolean;
  isCompanyGroupAdmin: () => boolean;
  isTrainer: () => boolean;
  isManager: () => boolean;
  isAdmin: () => boolean;
  /**
   * Does the current user hold this delegated capability? A company ADMIN holds every capability
   * (the server says so), so call sites ask `can(X)` alone rather than `isCompanyAdmin() || can(X)`.
   */
  can: (capability: Capability) => boolean;
  impersonate: (userId: number, displayName?: string) => Promise<void>;
  stopImpersonating: () => Promise<void>;
  clearImpersonation: () => void;
  isImpersonating: () => boolean;
  impersonatedName: string | null;
}

const AuthContext = React.createContext<AuthContextValue>(undefined as unknown as AuthContextValue);

// While impersonating, the SYSTEM_ADMIN's OWN login token is parked here and the impersonation
// token becomes the active `auth`. Presence of this key IS the "currently impersonating" signal.
const ORIGINAL_AUTH_KEY = 'authOriginal';
const IMPERSONATED_NAME_KEY = 'impersonatedName';
// The current identity's authorities (from /user/me), mirrored here so non-React modules can read
// them. Kept in sync by AuthProvider on every identity change (incl. impersonation).
const AUTHORITIES_KEY = 'authorities';
// The current identity's delegated capabilities (from /user/me), mirrored alongside the authorities
// for the same reason: non-React modules can't use the hook. Never read from the JWT — capabilities
// are resolved per request server-side precisely because a 180-day token cannot be revoked.
const CAPABILITIES_KEY = 'capabilities';

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}

/**
 * Role check for non-React modules (service files) that can't use the useAuth() hook.
 * Prefers the DB-derived authorities that /user/me returns (mirrored to localStorage by
 * AuthProvider) and falls back to the JWT `roles` claim. The DB-derived source is authoritative:
 * the `roles` claim only exists on tokens minted after it was introduced, so a legacy token (valid
 * up to the 180-day TTL) lacks it — a JWT-only check would then misclassify a real SYSTEM_ADMIN.
 */
export function isSuperAdminAuthority(): boolean {
  return hasAuthority("SYSTEM_ADMIN");
}

/**
 * Non-React authority check for the COMPANY_GROUP_ADMIN persona, mirroring {@link isSuperAdminAuthority}.
 * Service files (e.g. ExcersiceService / TemplatesService) use it to route content calls to the
 * owner-scoped {@code /groupAdmin/*} endpoints instead of {@code /system/*} or {@code /admin/*}.
 */
export function isCompanyGroupAdminAuthority(): boolean {
  return hasAuthority("COMPANY_GROUP_ADMIN");
}

/** Shared authority lookup: DB-derived authorities (authoritative) with a JWT `roles` fallback. */
function hasAuthority(authority: string): boolean {
  try {
    const stored = localStorage.getItem(AUTHORITIES_KEY);
    if (stored) {
      const authorities = JSON.parse(stored);
      if (Array.isArray(authorities)) return authorities.includes(authority);
    }
    const token = localStorage.getItem("auth");
    if (!token) return false;
    const roles = JSON.parse(atob(token.split(".")[1])).roles || [];
    return Array.isArray(roles) && roles.includes(authority);
  } catch {
    return false;
  }
}

export function AuthProvider({ children }: { children?: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<AuthUser | undefined>();
  const [loading, setLoading] = useState(true);
  // Non-null when the super-admin is impersonating (the display name of the impersonated user).
  const [impersonatedName, setImpersonatedName] = useState<string | null>(
      () => localStorage.getItem(IMPERSONATED_NAME_KEY) || null);

  // Mirror the DB-derived authorities (from /user/me) into localStorage on every identity change
  // (login, refresh, impersonate, stop). Non-React modules (service files that can't use this hook)
  // read AUTHORITIES_KEY to decide role-based routing — see getAuthorities() below. This is the
  // reliable source: the JWT `roles` claim is only present on tokens minted after that claim was
  // added, so a legacy token (up to the 180-day TTL) lacks it and JWT-only role checks silently fail.
  useEffect(() => {
    if (currentUser?.authorities) {
      localStorage.setItem(AUTHORITIES_KEY, JSON.stringify(currentUser.authorities));
    }
    if (currentUser?.capabilities) {
      localStorage.setItem(CAPABILITIES_KEY, JSON.stringify(currentUser.capabilities));
    }
    // Tag Sentry events with who hit them. Without this every error is anonymous and we can't
    // tell "one company is broken" from "the platform is broken". Cleared on logout (undefined).
    setSentryUser(currentUser);
  }, [currentUser]);

  const login = (user: AuthUser | undefined, token: string) => {
    if (!user?.authorities?.some(x => (ADMIN_APP_ROLES as readonly string[]).includes(x)))
      throw new Error("Access denied");

    setCurrentUser(user);
    localStorage.setItem('auth', token);
  };

  // Tear down any impersonation locally: drop the parked super-admin token + name and reset the React
  // state that the banner reads. Pure client cleanup — no API call (unlike stopImpersonating, which
  // also audits the exit and restores the SA session). Used by logout AND by the banner's self-heal
  // when it finds orphaned impersonation keys with no logged-in user.
  const clearImpersonation = () => {
    localStorage.removeItem(ORIGINAL_AUTH_KEY);
    localStorage.removeItem(IMPERSONATED_NAME_KEY);
    setImpersonatedName(null);
  };

  const logout = () => {
    localStorage.removeItem('auth');
    localStorage.removeItem(AUTHORITIES_KEY);
    localStorage.removeItem(CAPABILITIES_KEY);
    // Also tear down any active impersonation. Otherwise the parked super-admin token (ORIGINAL_AUTH_KEY)
    // survives logout — so isImpersonating() (a raw localStorage read) stays true and the purple
    // ImpersonationBanner keeps rendering over the login screen, AND a privileged SA token lingers in
    // storage after sign-out.
    clearImpersonation();
    setCurrentUser(undefined);
  };

  const isSuperAdmin = (): boolean => {
    return !!currentUser?.authorities?.some(element => element === "SYSTEM_ADMIN");
  };

  const isCompanyAdmin = (): boolean => {
    return !!currentUser?.authorities?.some(element => element === ROLES.ADMIN);
  };

  /**
   * A company user holding delegated authority — above a trainer, below a company admin. What they
   * may actually do is per-user; ask can() rather than assuming anything from the role alone.
   */
  const isManager = (): boolean => {
    return !!currentUser?.authorities?.some(element => element === ROLES.MANAGER);
  };

  const can = (capability: Capability): boolean => {
    return !!currentUser?.capabilities?.some(element => element === capability);
  };

  // A company-less persona that manages a curated set of companies via impersonation. Distinct from
  // SYSTEM_ADMIN: in the Admin UI they only get the Dashboard + Companies sections (see SideNav).
  const isCompanyGroupAdmin = (): boolean => {
    return !!currentUser?.authorities?.some(element => element === "COMPANY_GROUP_ADMIN");
  };

  const isAdmin = (): boolean => {
    return isCompanyAdmin() || isSuperAdmin();
  }

  const isTrainer = (): boolean => {
    return !!currentUser?.authorities?.some(element => element === "TRAINER");
  };

  const refetchUser = () => {
    setLoading(true);
    const token = localStorage.getItem('auth');
    getUser(token).then(res => {
      setCurrentUser(res.data?.body);
      setLoading(false);
    }).catch(() => {
      setLoading(false)
    })
  }

  const isImpersonating = () => !!localStorage.getItem(ORIGINAL_AUTH_KEY);

  // Start impersonating a company ADMIN/TRAINER. Parks the super-admin's own token, swaps in the
  // short-lived impersonation token, and reloads the user — the whole app then renders AS that user.
  const impersonate = async (userId: number, displayName?: string) => {
    const res = await impersonateUserApi(userId);
    const impersonationToken = res.data?.body;
    if (!impersonationToken) throw new Error("No impersonation token returned");

    // Park the SA's own token (only on first hop, so nested calls can't lose the original).
    if (!localStorage.getItem(ORIGINAL_AUTH_KEY)) {
      localStorage.setItem(ORIGINAL_AUTH_KEY, localStorage.getItem('auth') ?? '');
    }
    localStorage.setItem('auth', impersonationToken);
    if (displayName) localStorage.setItem(IMPERSONATED_NAME_KEY, displayName);
    setImpersonatedName(displayName || 'משתמש');

    const me = await getUser(impersonationToken);
    setCurrentUser(me.data?.body);
  };

  // Return to the super-admin session: restore the parked token and reload the real SA user.
  const stopImpersonating = async () => {
    const original = localStorage.getItem(ORIGINAL_AUTH_KEY);
    // Best-effort audit of the exit — called with the SUPER-ADMIN's own token (the active token is
    // still the impersonation one, which the SYSTEM_ADMIN-only /stop endpoint would reject). Never
    // block the restore on it.
    try { await stopImpersonationApi(original); } catch (e) { /* ignore */ }

    if (original) {
      localStorage.setItem('auth', original);
      localStorage.removeItem(ORIGINAL_AUTH_KEY);
    }
    localStorage.removeItem(IMPERSONATED_NAME_KEY);
    setImpersonatedName(null);

    const token = localStorage.getItem('auth');
    const me = await getUser(token);
    setCurrentUser(me.data?.body);
  };

  useEffect( () => {
    setLoading(true);
    const token = localStorage.getItem('auth');
    if (token != null) {
      getUser(token).then(user => {
        login(user.data?.body, token);
        setLoading(false);
      }).catch(() => {
        setLoading(false);
      })
    } else {
      setLoading(false);
    }
  }, []);


  const value = {
    currentUser,
    refetchUser,
    login,
    logout,
    isSuperAdmin,
    isCompanyAdmin,
    isCompanyGroupAdmin,
    isTrainer,
    isManager,
    can,
    isAdmin,
    impersonate,
    stopImpersonating,
    clearImpersonation,
    isImpersonating,
    impersonatedName,
  };

  return (
      <AuthContext.Provider value={value}>
        {!loading && children}
      </AuthContext.Provider>
  );
}
